import React, { Dispatch, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Formik } from 'formik'
import * as Yup from 'yup'

import { ProductIds } from 'core'
import { Expense } from 'core/application/expenses/getUserAvailableExpenseTypes'

import RedAsterisk from 'common/components/dataEntry/components/RedAsterisk'
import Radio from 'common/components/dataEntry/formik/FormikRadioField'
import Modal from 'common/components/feedback/Modal'

import { Alert, Col, Row, Skeleton } from 'common/components'
import { useLoginUserV2 } from 'common/hooks'

type ExpenseQuestionaryModalProps = {
  isModalVisible: boolean
  setIsModalVisible: Dispatch<boolean>
  record: Expense
  onFormSubmit: (isValid: boolean, premiumsViaAPTC: string) => void
  loading?: boolean
}

export type ExpenseQuestionaryValues = {
  primaryPolicyHolder: 'me' | 'other'
  premiumsViaPayroll: 'yes' | 'no'
  premiumsViaAPTC: 'yes' | 'no'
  premiumsViaPayrollFromAnotherEmployer: 'yes' | 'no'
}

function ExpenseQuestionaryModal({
  isModalVisible,
  setIsModalVisible,
  record,
  onFormSubmit,
  loading = false,
}: ExpenseQuestionaryModalProps) {
  const { t: translate } = useTranslation()
  const { t: eeTranslate } = useTranslation(['ee-dashboard', 'translation'])
  const { user } = useLoginUserV2()

  const isIchra = user.productId === ProductIds.IchraId

  const formikRef = useRef<any>(null)
  const [initialValues] = useState({
    primaryPolicyHolder: null,
    premiumsViaPayroll: null,
    premiumsViaAPTC: null,
    premiumsViaPayrollFromAnotherEmployer: null,
    productId: user.productId,
    isSpousePremium: record.isSpousePremium,
  })

  const onOk = () => {
    formikRef?.current?.submitForm()
  }

  const onCancel = () => {
    formikRef?.current?.resetForm()
    setIsModalVisible(false)
  }

  const onSubmit = async (values: ExpenseQuestionaryValues) => {
    const { primaryPolicyHolder, premiumsViaPayroll, premiumsViaAPTC, premiumsViaPayrollFromAnotherEmployer } = values
    const result = record.isSpousePremium
      ? primaryPolicyHolder === 'me' && premiumsViaPayroll === 'yes' && premiumsViaPayrollFromAnotherEmployer === 'yes'
      : primaryPolicyHolder === 'me' && premiumsViaPayroll === 'no'

    onFormSubmit(result, premiumsViaAPTC)
    formikRef?.current?.resetForm()
    setIsModalVisible(false)
  }

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      validationSchema={formValidator(eeTranslate)}
      onSubmit={onSubmit}
      isInitialValid={false}
      validateOnChange={true}
    >
      {({ isValid }) => {
        return (
          <Modal
            title={`${eeTranslate('get-reimburse.premium-expense-questionary-modal.title')} ${record.name}`}
            closable
            okText={translate('general.next')}
            cancelText={translate('general.cancel')}
            okButtonProps={{
              disabled: !isValid,
            }}
            onOk={onOk}
            onCancel={onCancel}
            visible={isModalVisible}
            destroyOnClose={true}
          >
            <Row gutter={10}>
              <Skeleton loading={loading}>
                <Col span={24} className="mb-24">
                  <Row gutter={8}>
                    <Col span={24}>
                      <div className="flex mb-8">
                        <RedAsterisk className="mr-4" />
                        <span>
                          {eeTranslate('get-reimburse.premium-expense-questionary-modal.primary-policy-question')}
                        </span>
                      </div>
                    </Col>
                  </Row>
                  <Radio.Group name="primaryPolicyHolder" className="w-100">
                    <Row gutter={8}>
                      <Col span={24}>
                        <Radio value={'me'}>
                          {eeTranslate('get-reimburse.premium-expense-questionary-modal.primary-policy-option-1')}
                        </Radio>
                      </Col>
                      <Col span={24}>
                        <Radio value={'other'}>
                          {eeTranslate('get-reimburse.premium-expense-questionary-modal.primary-policy-option-2')}
                        </Radio>
                      </Col>
                    </Row>
                  </Radio.Group>
                </Col>

                <Col span={24} className="mb-24">
                  <Row gutter={8}>
                    <Col span={24}>
                      <div className="flex mb-8">
                        <RedAsterisk className="mr-4" />
                        <span>
                          {eeTranslate('get-reimburse.premium-expense-questionary-modal.premium-payroll-question')}
                        </span>
                      </div>
                    </Col>
                  </Row>
                  <Radio.Group name="premiumsViaPayroll" className="w-100">
                    <Row gutter={8}>
                      <Col span={24}>
                        <Radio value={'yes'}>
                          {eeTranslate('get-reimburse.premium-expense-questionary-modal.premium-payroll-option-1')}
                        </Radio>
                      </Col>
                      <Col span={24}>
                        <Radio value={'no'}>
                          {eeTranslate('get-reimburse.premium-expense-questionary-modal.premium-payroll-option-2')}
                        </Radio>
                      </Col>
                    </Row>
                  </Radio.Group>
                </Col>
                {isIchra && (
                  <>
                    <Col span={24} className="mb-24">
                      <Row gutter={8}>
                        <Col span={24}>
                          <div className="flex mb-8">
                            <RedAsterisk className="mr-4" />
                            <span>
                              {eeTranslate('get-reimburse.premium-expense-questionary-modal.premium-aptc-question')}
                            </span>
                          </div>
                        </Col>
                      </Row>
                      <Radio.Group name="premiumsViaAPTC" className="w-100">
                        <Row gutter={8}>
                          <Col span={24}>
                            <Radio value={'yes'}>
                              {eeTranslate('get-reimburse.premium-expense-questionary-modal.premium-aptc-option-1')}
                            </Radio>
                          </Col>
                          <Col span={24}>
                            <Radio value={'no'}>
                              {eeTranslate('get-reimburse.premium-expense-questionary-modal.premium-aptc-option-2')}
                            </Radio>
                          </Col>
                        </Row>
                      </Radio.Group>
                    </Col>
                    <Col span={24} className="mb-24">
                      <Row gutter={8}>
                        <Col span={24}>
                          <Alert
                            type="info"
                            showIcon
                            message={'What are individual premium tax credits?'}
                            description={
                              <>
                                <p>
                                  Tax credits are available for individuals and families who meet certain income
                                  requirements and do not have access to AC-defined “affordable” health insurance
                                  through an "eligible employer-sponsored plan" or another government program.
                                </p>
                                <p>
                                  Employees participating in an ICHRA are prohibited from accessing premium tax credits.
                                </p>
                              </>
                            }
                          />
                        </Col>
                      </Row>
                    </Col>
                  </>
                )}

                {record.isSpousePremium && (
                  <Col span={24}>
                    <Row gutter={8}>
                      <Col span={24}>
                        <div className="flex mb-8">
                          <RedAsterisk className="mr-4" />
                          <span>
                            {eeTranslate('get-reimburse.premium-expense-questionary-modal.premium-spouse-question')}
                          </span>
                        </div>
                      </Col>
                    </Row>
                    <Radio.Group name="premiumsViaPayrollFromAnotherEmployer" className="w-100">
                      <Row gutter={8}>
                        <Col span={24}>
                          <Radio value={'yes'}>
                            {eeTranslate('get-reimburse.premium-expense-questionary-modal.premium-spouse-option-1')}
                          </Radio>
                        </Col>
                        <Col span={24}>
                          <Radio value={'no'}>
                            {eeTranslate('get-reimburse.premium-expense-questionary-modal.premium-spouse-option-2')}
                          </Radio>
                        </Col>
                      </Row>
                    </Radio.Group>
                  </Col>
                )}
              </Skeleton>
            </Row>
          </Modal>
        )
      }}
    </Formik>
  )
}

export default ExpenseQuestionaryModal

const formValidator = (eeTranslate: any) =>
  Yup.object({
    isSpousePremium: Yup.boolean(),
    primaryPolicyHolder: Yup.string()
      .nullable()
      .required(eeTranslate('get-reimburse.premium-expense-questionary-modal.error')),
    premiumsViaPayroll: Yup.string()
      .nullable()
      .required(eeTranslate('get-reimburse.premium-expense-questionary-modal.error')),
    premiumsViaAPTC: Yup.string()
      .nullable()
      .when('productId', {
        is: ProductIds.IchraId,
        then: Yup.string().required(eeTranslate('get-reimburse.premium-expense-questionary-modal.error')),
      }),
    premiumsViaPayrollFromAnotherEmployer: Yup.string()
      .nullable()
      .when('isSpousePremium', {
        is: true,
        then: Yup.string().required(eeTranslate('get-reimburse.premium-expense-questionary-modal.error')),
      }),
  })
